import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
export const CLIENT_EXPLORER_VIEWS = {
  portfolio: 'portfolio',
  generic_portfolio: 'generic_portfolio',
  model_portfolio: 'model_portfolio',
  benchmark_portfolio: 'benchmark_portfolio',
  prospect: 'prospect',
  client: 'client',
  group: 'group',
  legal_entity: 'legal_entity',
  account: 'account',
};
const ENTITY_TYPE_TO_VIEWS = {
  'Portfolio': CLIENT_EXPLORER_VIEWS.portfolio,
  'Generic Portfolio': CLIENT_EXPLORER_VIEWS.generic_portfolio,
  'Model Portfolio': CLIENT_EXPLORER_VIEWS.model_portfolio,
  'Benchmark Portfolio': CLIENT_EXPLORER_VIEWS.benchmark_portfolio,
  'Prospect': CLIENT_EXPLORER_VIEWS.prospect,
  'Client': CLIENT_EXPLORER_VIEWS.client,
  'Group': CLIENT_EXPLORER_VIEWS.group,
  'Legal Entity': CLIENT_EXPLORER_VIEWS.legal_entity,
  'Account': CLIENT_EXPLORER_VIEWS.account,
};
const getViewFromEntityType = entityType => (ENTITY_TYPE_TO_VIEWS[entityType]);
const getEntityTypesFromView = view => {
  if (view === CLIENT_EXPLORER_VIEWS.portfolio) return ['Generic Portfolio', 'Model Portfolio', 'Benchmark Portfolio'];
  const entityType = Object.keys(ENTITY_TYPE_TO_VIEWS).find(key => ENTITY_TYPE_TO_VIEWS[key] === view);
  return [entityType];
};

export const useClientExplorerStore = defineStore('clientExplorer', () => {
  const currentView = ref(CLIENT_EXPLORER_VIEWS.client);
  const selectedEntityId = ref(null);
  const entities = ref(new Object());
  const isLoading = ref(false);
  // init hasFetched to object with each entity type set to false
  const hasFetched = ref(Object.keys(ENTITY_TYPE_TO_VIEWS).filter(entityType => entityType !== 'Portfolio').reduce((result, entityType)=> {
    result[entityType] = false; return result;
  }, {}));

  const generic_portfolios = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Generic Portfolio'));
  const model_portfolios = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Model Portfolio'));
  const benchmark_portfolios = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Benchmark Portfolio'));
  const all_portfolios = computed(() => [...generic_portfolios.value, ...model_portfolios.value, ...benchmark_portfolios.value]);

  const prospects = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Prospect'));
  const clients = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Client'));
  const groups = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Group'));
  const legal_entities = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Legal Entity'));
  const accounts = computed(() => Object.values(entities.value || {}).filter(entity => entity.entity_type === 'Account'));
  const results = computed(() => ({
    generic_portfolio: generic_portfolios.value,
    model_portfolio: model_portfolios.value,
    benchmark_portfolio: benchmark_portfolios.value,
    portfolio: all_portfolios.value,
    prospect: prospects.value,
    client: clients.value,
    group: groups.value,
    legal_entity: legal_entities.value,
    account: accounts.value,
  }[currentView.value]));
  const detailView = computed(() => !!selectedEntity.value);
  const selectedEntity = computed(() => selectedEntityId.value ? entities.value[selectedEntityId.value] : null);

  watch(selectedEntity, () => {
    const entityType = selectedEntity?.value?.entity_type;
    if (!entityType) return;
    const expectedView = getViewFromEntityType(entityType);
    if (expectedView !== currentView.value) setView(expectedView);
  });
  watch(currentView, async () => {
    await fetchNecessaryEntitiesForView();
  });

  async function fetchNecessaryEntitiesForView() {
    console.log('fetchNecessaryEntitiesForView called');
    const entityTypesToFetch = getEntityTypesFromView(currentView.value).filter(entityType => hasFetched.value[entityType] === false);
    console.log('entityTypesToFetch:', entityTypesToFetch);
    if (entityTypesToFetch?.length > 0) await fetchEntities(entityTypesToFetch);
  }
  function setView(view) {
    console.log('called setView with view: ', view);
    currentView.value = view;
  }
  function selectEntity(entityId) {
    selectedEntityId.value = entityId;
  }
  function deselectEntity() {
    selectedEntityId.value = null;
  }
  function getEntity(entityId) {
    console.log('GET ENTITY IN STORE');
    console.log(entityId);
    console.log('ENTITIES');
    console.log(entities);
    return entities.value[entityId];
  }

  /**
   * Fetches entities for the current advisor.
   * If entityTypes is specified then the results will be limited to those types, otherwise entities of all types will be returned.
   * @param {object} entityTypes - optional list of entity types to fetch.
   */
  async function fetchEntities(entityTypes=[]) {
    console.log('fetchEntities called with:', entityTypes);
    isLoading.value = true;
    try {
      const requestUrl = entityTypes.length ? `/api/v1/entity/?entity_types=${entityTypes.join(',')}` : '/api/v1/entity/';
      console.log(`Fetching from URL: ${requestUrl}`);
      const res = await fetch(requestUrl, {
        method: 'GET',
        headers: { 'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value },
      });
      const data = await res.json();
      console.log('Received data:', data);
      // NOTE: we will probably want to optimize some of the computeds towards the top and also add pagination to this endpoint and to the entity list
      const formattedResponseData = data.entities?.reduce((result, curr) => {
        result[curr['id']] = curr; return result;
      }, {});
      entities.value = { ...entities.value, ...formattedResponseData };
      isLoading.value = false;

      if (entityTypes.length) entityTypes.forEach(entityType => hasFetched.value[entityType] = true);
      else Object.keys(hasFetched).forEach(entityType => hasFetched.value[entityType] = true);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching entities:', error);
    } finally {
      isLoading.value = false;
    }
  }

  fetchNecessaryEntitiesForView();

  return {
    currentView,
    selectedEntity,
    selectedEntityId,
    entities,
    isLoading,
    results,
    detailView,
    setView,
    selectEntity,
    deselectEntity,
    getEntity,
    fetchEntities,

    prospects,
    clients,
    groups,
    legal_entities,
    accounts,
    generic_portfolios,
    model_portfolios,
    benchmark_portfolios,
    all_portfolios,
  };
});
